import React, { useCallback, useEffect, useState } from 'react'
import {
  Settings,
  MessageSquare,
  RefreshCw,
  Loader2,
  Play,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { platformAPI, supportReportAPI } from '../services/api'
import { CollaborationProgramsCard } from '../components/CollaborationProgramsCard'
import { CrmSyncCard } from '../components/CrmSyncCard'
import { useAuth } from '../contexts/AuthContext'

const KEYS = {
  enabled: 'support_report.enabled',
  tenantId: 'support_report.tenant_id',
  groupChatId: 'support_report.group_chat_id',
  groupName: 'support_report.group_name',
  realertPolicy: 'support_report.realert_policy',
  days: 'support_report.days',
}

const ACTIVE_SESSION_STATUSES = ['working', 'connected', 'ready']

const statusBadge = (status) => {
  const map = {
    sent: 'bg-emerald-100 text-emerald-800',
    no_flags: 'bg-slate-200 text-slate-700',
    skipped_config: 'bg-amber-100 text-amber-800',
    skipped_window: 'bg-slate-200 text-slate-700',
    failed: 'bg-red-100 text-red-800',
  }
  return map[status] || 'bg-gray-100 text-gray-800'
}

const statusLabel = (status) => {
  const map = {
    sent: 'Enviado',
    no_flags: 'Sem alertas',
    skipped_config: 'Ignorado (configuração)',
    skipped_window: 'Ignorado (fora do horário)',
    failed: 'Falhou',
  }
  return map[status] || status
}

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

const TRIGGER_LABEL = { cron: 'agendada', manual: 'manual' }

const ReportRow = ({ report, expanded, onToggle }) => (
  <div className="border border-admin-200 rounded-lg">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 text-left hover:bg-admin-50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 ${statusBadge(
            report.status
          )}`}
        >
          {statusLabel(report.status)}
        </span>
        <span className="text-xs text-admin-600 truncate">
          {formatDate(report.created_at)} · {TRIGGER_LABEL[report.trigger] || report.trigger} · {report.flagged_count}/
          {report.chat_count} sinalizadas
        </span>
      </div>
      {expanded ? (
        <ChevronUp className="h-4 w-4 text-admin-400 flex-shrink-0 ml-2" />
      ) : (
        <ChevronDown className="h-4 w-4 text-admin-400 flex-shrink-0 ml-2" />
      )}
    </button>
    {expanded && (
      <div className="border-t border-admin-200 p-4 bg-admin-50 space-y-3">
        {report.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {report.error}
          </div>
        )}
        {report.report_text ? (
          <pre className="whitespace-pre-wrap font-sans text-sm text-admin-800 bg-white border border-admin-200 rounded-lg p-3">
            {report.report_text}
          </pre>
        ) : (
          <p className="text-sm text-admin-500">Nenhuma mensagem foi enviada nesta execução.</p>
        )}
      </div>
    )}
  </div>
)

export const SettingsPage = () => {
  const [form, setForm] = useState({
    enabled: false,
    tenantId: '',
    groupChatId: '',
    groupName: '',
    realertPolicy: 'always',
    days: 'weekdays',
  })
  // Staff: read-only view of the settings and the report history
  const { isOwner } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const [tenants, setTenants] = useState([])
  const [groups, setGroups] = useState([])
  const [session, setSession] = useState(null)
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [groupsError, setGroupsError] = useState(null)

  const [reports, setReports] = useState([])
  const [reportsLoading, setReportsLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [expandedReport, setExpandedReport] = useState(null)

  const loadReports = useCallback(async () => {
    setReportsLoading(true)
    try {
      const res = await supportReportAPI.getReports()
      setReports(res.data?.data || [])
    } catch {
      /* history is non-critical */
    } finally {
      setReportsLoading(false)
    }
  }, [])

  const loadGroups = useCallback(async (tenantId, sync = false) => {
    if (!tenantId) {
      setGroups([])
      setSession(null)
      return
    }
    setGroupsLoading(true)
    setGroupsError(null)
    try {
      const res = await supportReportAPI.getGroups(tenantId, sync)
      setGroups(res.data?.data?.groups || [])
      setSession(res.data?.data?.session || null)
    } catch (err) {
      setGroups([])
      setSession(null)
      setGroupsError(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          'Falha ao carregar os grupos deste estúdio (ele tem uma sessão de WhatsApp?)'
      )
    } finally {
      setGroupsLoading(false)
    }
  }, [])

  useEffect(() => {
    let canceled = false
    const boot = async () => {
      setLoading(true)
      setError(null)
      try {
        const [settingsRes, tenantsRes] = await Promise.all([
          platformAPI.getPlatformSettings(),
          platformAPI.getTenants({ per_page: 200 }),
        ])
        if (canceled) return
        const data = settingsRes.data?.data || {}
        const tenantId = data[KEYS.tenantId] || ''
        setForm({
          enabled: Boolean(data[KEYS.enabled]),
          tenantId,
          groupChatId: data[KEYS.groupChatId] || '',
          groupName: data[KEYS.groupName] || '',
          realertPolicy: data[KEYS.realertPolicy] || 'always',
          days: data[KEYS.days] || 'weekdays',
        })
        setTenants(tenantsRes.data?.data || tenantsRes.data?.tenants || [])
        if (tenantId && isOwner) loadGroups(tenantId)
      } catch (err) {
        if (!canceled) {
          setError(err.response?.data?.error?.message || err.message || 'Falha ao carregar as configurações')
        }
      } finally {
        if (!canceled) setLoading(false)
      }
    }
    boot()
    loadReports()
    return () => {
      canceled = true
    }
  }, [loadGroups, loadReports, isOwner])

  const handleTenantChange = (tenantId) => {
    setForm((f) => ({ ...f, tenantId, groupChatId: '', groupName: '' }))
    loadGroups(tenantId)
  }

  const handleGroupChange = (chatId) => {
    const group = groups.find((g) => g.chat_id === chatId)
    setForm((f) => ({ ...f, groupChatId: chatId, groupName: group?.name || '' }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      await platformAPI.updatePlatformSettings({
        [KEYS.enabled]: form.enabled,
        [KEYS.tenantId]: form.tenantId || null,
        [KEYS.groupChatId]: form.groupChatId || null,
        [KEYS.groupName]: form.groupName || null,
        [KEYS.realertPolicy]: form.realertPolicy,
        [KEYS.days]: form.days,
      })
      setNotice('Configurações salvas.')
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Falha ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const handleRunNow = async () => {
    setRunning(true)
    setError(null)
    setNotice(null)
    try {
      const res = await supportReportAPI.runNow()
      const result = res.data?.data || {}
      setNotice(
        `Execução concluída: ${statusLabel(result.status)} — ${result.flagged_count ?? 0}/${
          result.chat_count ?? 0
        } conversas sinalizadas.`
      )
      await loadReports()
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Falha na execução')
    } finally {
      setRunning(false)
    }
  }

  const configComplete = form.enabled && form.tenantId && form.groupChatId
  const sessionWarning =
    session &&
    (session.engine === 'DEMO'
      ? 'Este estúdio tem uma sessão de WhatsApp DEMO — o envio é desativado para sessões de demonstração.'
      : !ACTIVE_SESSION_STATUSES.includes((session.status || '').toLowerCase())
        ? `O status da sessão do WhatsApp é "${session.status}" — os dados das conversas podem estar desatualizados e os envios podem falhar.`
        : null)

  // keep a saved group selectable even if it's missing from the loaded list
  const groupOptions =
    form.groupChatId && !groups.some((g) => g.chat_id === form.groupChatId)
      ? [{ chat_id: form.groupChatId, name: form.groupName || form.groupChatId }, ...groups]
      : groups

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="p-8 text-center text-admin-600">Carregando…</div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-admin-900">Configurações da plataforma</h1>
          <p className="text-admin-600 text-sm">
            Configuração geral da plataforma para os super admins.
            {!isOwner && ' Somente leitura para a equipe.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {notice}
        </div>
      )}

      {isOwner && <CrmSyncCard />}

      <CollaborationProgramsCard readOnly={!isOwner} />

      <div className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-admin-900">Relatório de triagem do suporte no WhatsApp</h2>
        </div>
        <p className="text-sm text-admin-600 mb-6">
          Analisa as conversas do número de suporte dos últimos 14 dias e envia um resumo de triagem
          para um grupo de WhatsApp às 09:00, 12:00, 15:00 e 18:00 (horário de São Paulo). Nada é enviado
          quando nenhuma conversa precisa de atenção.
        </p>

        <fieldset className="space-y-5" disabled={!isOwner}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-admin-300 text-primary-600 focus:ring-primary-500"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            <span className="text-sm font-medium text-admin-700">Ativar relatório agendado</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Sessão de WhatsApp (estúdio)
              </label>
              <select
                className="input w-full"
                value={form.tenantId}
                onChange={(e) => handleTenantChange(e.target.value)}
              >
                <option value="">Selecione um estúdio…</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
              <p className="text-xs text-admin-500 mt-1">
                O estúdio cujo número de WhatsApp conectado recebe as conversas de suporte.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Grupo de destino
              </label>
              <div className="flex gap-2">
                <select
                  className="input w-full"
                  value={form.groupChatId}
                  onChange={(e) => handleGroupChange(e.target.value)}
                  disabled={!form.tenantId || groupsLoading}
                >
                  <option value="">
                    {groupsLoading ? 'Carregando grupos…' : 'Selecione um grupo…'}
                  </option>
                  {groupOptions.map((g) => (
                    <option key={g.chat_id} value={g.chat_id}>
                      {g.name || g.chat_id}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary inline-flex items-center gap-1 whitespace-nowrap"
                  onClick={() => loadGroups(form.tenantId, true)}
                  disabled={!form.tenantId || groupsLoading}
                  title="Atualizar a lista de grupos do WhatsApp"
                >
                  {groupsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </button>
              </div>
              {groupsError && <p className="text-xs text-red-600 mt-1">{groupsError}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Política de realerta
              </label>
              <select
                className="input w-full"
                value={form.realertPolicy}
                onChange={(e) => setForm((f) => ({ ...f, realertPolicy: e.target.value }))}
              >
                <option value="always">Repetir até ser respondida</option>
                <option value="new_messages_only">Somente nova atividade</option>
              </select>
              <p className="text-xs text-admin-500 mt-1">
                "Repetir até ser respondida" volta a sinalizar as conversas sem atendimento a cada execução (marcadas como
                "reincidente").
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">Dias</label>
              <select
                className="input w-full"
                value={form.days}
                onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))}
              >
                <option value="weekdays">Somente dias úteis</option>
                <option value="all">Todos os dias</option>
              </select>
              <p className="text-xs text-admin-500 mt-1">
                Horários do relatório: 09:00 / 12:00 / 15:00 / 18:00 America/Sao_Paulo.
              </p>
            </div>
          </div>

          {sessionWarning && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {sessionWarning}
            </div>
          )}

          {isOwner && (
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              className="btn btn-primary inline-flex items-center gap-2"
              onClick={handleSave}
              disabled={saving}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar configurações
            </button>
            <button
              type="button"
              className="btn btn-secondary inline-flex items-center gap-2"
              onClick={handleRunNow}
              disabled={!configComplete || running || saving}
              title={
                configComplete
                  ? 'Analisar as conversas e enviar o relatório agora'
                  : 'Ative o relatório e selecione um estúdio e um grupo primeiro (salve antes de executar)'
              }
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {running ? 'Executando…' : 'Executar agora'}
            </button>
          </div>
          )}
        </fieldset>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-admin-900">Execuções recentes</h2>
          <button
            type="button"
            className="p-1.5 rounded hover:bg-admin-100 text-admin-600"
            onClick={loadReports}
            title="Atualizar"
          >
            <RefreshCw className={`h-4 w-4 ${reportsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {reports.length === 0 ? (
          <p className="text-sm text-admin-500">Nenhuma execução ainda.</p>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => (
              <ReportRow
                key={r.id}
                report={r}
                expanded={expandedReport === r.id}
                onToggle={() => setExpandedReport(expandedReport === r.id ? null : r.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
